import { Coordinate, TileSize } from "game-common/models"
import { HILL_LEVEL_MAX } from "game-common/room/hill_height"

/**
 * Invert the same vertical lift used to render the selected building layer. A building on a hill's tier is drawn up by
 * `liftAt(cell)` more (openspec world/hills, "Aiming on a hill"), so the cells below the flat one are tried southernmost
 * first and the first whose drawn footprint holds the point is the one; none does, the flat cell.
 */
export const buildingPlaneTarget = (world: Coordinate, yOffset = 0, liftAt?: (cx: number, cy: number) => number): Coordinate => {
    const flat = { x: Math.floor(world.x / TileSize), y: Math.floor((world.y - yOffset) / TileSize) }
    if (!liftAt) return flat
    for (let dy = HILL_LEVEL_MAX; dy >= 0; dy--) {
        const y = flat.y + dy
        if (Math.floor((world.y - yOffset + liftAt(flat.x, y)) / TileSize) === y) return { x: flat.x, y }
    }
    return flat
}

// How finely the ground under a point is searched for, in px; the crossing between two steps is interpolated.
const GROUND_STEP_PX = 2

/**
 * The ground drawn at `world`, on a hill drawn `heightAt` above itself (openspec world/hills): its art draws ground row g at
 * screen row g - heightAt(g), a row further south over the rows north of it, so this is the southernmost ground in the
 * point's column drawn on the point's row. A slope's face is found where it shows, however stretched or foreshortened;
 * a cell treated as a box lifted whole would leave a south-facing face, drawn a level taller than its ground, belonging
 * to no cell. `maxHeight`: the most the hill rises anywhere. Flat ground (height 0) is the point itself.
 */
export const groundDrawnUnder = (world: Coordinate, heightAt: (x: number, y: number) => number, maxHeight: number): Coordinate => {
    const screen = (g: number) => g - heightAt(world.x, g)
    // This far south, ground is drawn below the point however high it stands; at the point's own row, at or above it.
    let south = world.y + maxHeight + 1, southRow = screen(south)
    for (let g = south - GROUND_STEP_PX; g > world.y - 2 * GROUND_STEP_PX; g -= GROUND_STEP_PX) {
        const row = screen(g)
        if (row <= world.y) return { x: world.x, y: g + (world.y - row) / (southRow - row) * (south - g) }
        south = g
        southRow = row
    }
    return world
}
